export const PIECE_UNIT = 'ST';

export const SALES_UNIT_CODES = ['ST', 'PAK', 'M2', 'M', 'KPL', 'KAR', 'OPA', 'ROL', 'KG', 'G', 'PAC'] as const;

export type SalesUnitCode = (typeof SALES_UNIT_CODES)[number];

const UNIT_ALIAS: Partial<Record<string, SalesUnitCode>> = {
  OPA: 'PAK',
  PAC: 'PAK',
};

export const normalizeSalesUnit = (code?: string | null): SalesUnitCode => {
  if (!code) {
    return PIECE_UNIT;
  }

  const upper = code.toUpperCase();
  const aliased = UNIT_ALIAS[upper] ?? upper;

  if ((SALES_UNIT_CODES as readonly string[]).includes(aliased)) {
    return aliased as SalesUnitCode;
  }

  return PIECE_UNIT;
};

export const resolveOrderUnit = (orderUnit?: string | null, quantityType?: string): SalesUnitCode => {
  if (orderUnit) {
    return normalizeSalesUnit(orderUnit);
  }

  return quantityType === 'package' ? 'PAK' : PIECE_UNIT;
};

export const resolvePriceUnit = (
  priceUnit?: string | null,
  unitType?: string,
  orderUnit?: string | null,
  quantityType?: string,
): SalesUnitCode => {
  if (priceUnit) {
    return normalizeSalesUnit(priceUnit);
  }

  if (unitType === 'square_meters') {
    return 'M2';
  }

  return resolveOrderUnit(orderUnit, quantityType);
};

export const shouldShowOrderUnitLabel = (orderUnit: string): boolean => normalizeSalesUnit(orderUnit) !== PIECE_UNIT;

export const shouldShowPriceConversion = (
  orderUnit: string,
  priceUnit: string,
  multiplication: string | number,
): boolean => {
  const multiplier = Number(multiplication);
  if (!Number.isFinite(multiplier) || multiplier <= 0) {
    return false;
  }

  return normalizeSalesUnit(orderUnit) !== normalizeSalesUnit(priceUnit) || multiplier !== 1;
};

export const shouldExpandQuantityField = (
  orderUnit: string,
  priceUnit: string,
  multiplication: string | number,
): boolean => shouldShowOrderUnitLabel(orderUnit) || shouldShowPriceConversion(orderUnit, priceUnit, multiplication);

export const formatConvertedQuantity = (quantity: number, multiplication: string | number, locale: string): string => {
  const value = Number(quantity) * Number(multiplication);
  if (!Number.isFinite(value)) {
    return '0';
  }

  return new Intl.NumberFormat(locale, { maximumFractionDigits: 2 }).format(value);
};

export const getConvertedQuantityValue = (quantity: number, multiplication: string | number): number => {
  const value = Number(quantity) * Number(multiplication);
  return Number.isFinite(value) ? value : 0;
};
